import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DismissFilled } from '@fluentui/react-icons';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Typography } from '@mui/material';
import { EditSystemForm } from '@ui/components/Forms';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { updateSystemDetail } from '@application/redux/SystemDetail/system-detail-slice';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
const EditSystemDialog = ({ isOpen, onClose, onSystemUpdated, loading = false }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const methods = useForm({
        defaultValues: {
            id: '',
            systemName: '',
            systemType: '',
            description: '',
            cameraUrl: '',
            jetsonId: ''
        },
        mode: 'onBlur'
    });
    const { reset, setValue } = methods;
    const { systemEditMode } = useSelector((state) => state.systemDetail);
    const handleClose = () => {
        onClose();
        methods.reset();
    };
    useEffect(() => {
        if (isOpen && systemEditMode) {
            // Populate form with edit data for updateSystemDetailCommand
            const formData = {
                id: systemEditMode.id || '',
                systemName: systemEditMode.systemName || '',
                systemType: systemEditMode.systemType || '',
                description: systemEditMode.description || '',
                cameraUrl: systemEditMode.cameraUrl || systemEditMode.liveStreamUrl || '',
                jetsonId: systemEditMode.jetsonId || ''
            };
            // Use setTimeout to ensure form is ready
            setTimeout(() => {
                reset(formData);
                // Also set individual values to ensure they're populated
                setValue('id', formData.id);
                setValue('systemName', formData.systemName);
                setValue('systemType', formData.systemType);
                setValue('description', formData.description);
                setValue('cameraUrl', formData.cameraUrl);
                setValue('jetsonId', formData.jetsonId);
            }, 100);
        }
    }, [isOpen, systemEditMode, reset, setValue]);
    // Reset form when dialog closes
    useEffect(() => {
        if (!isOpen) {
            methods.reset();
        }
    }, [isOpen, methods]);
    const handleFormSubmit = async (data) => {
        try {
            const response = await dispatch(updateSystemDetail(data));
            if (updateSystemDetail.fulfilled.match(response)) {
                const { isSuccess, message } = response.payload;
                if (isSuccess) {
                    toast.success(message || 'System updated successfully!');
                    // Call the callback to update the grid temporarily
                    if (onSystemUpdated) {
                        onSystemUpdated(data);
                    }
                    handleClose();
                }
                else {
                    toast.error(message);
                }
            }
            else if (updateSystemDetail.rejected.match(response)) {
                toast.error(response.payload);
            }
        }
        catch (error) {
            toast.error('Failed to update system');
        }
    };
    return (_jsxs(Dialog, { open: isOpen, onClose: handleClose, maxWidth: 'sm', PaperProps: {
            sx: {
                width: '564px'
            }
        }, fullWidth: true, "aria-labelledby": 'EditSystemDialog', onKeyDown: (e) => e.key === 'Enter' && methods.handleSubmit(handleFormSubmit)(), children: [_jsxs(DialogTitle, { id: 'edit-system-dialog', sx: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }, children: [_jsx(Typography, { variant: 'h6', color: 'text.primary', children: "Edit System" }), _jsx(IconButton, { onClick: handleClose, children: _jsx(DismissFilled, { fontSize: 18 }) })] }), _jsx(Divider, {}), _jsx(DialogContent, { children: _jsx(Box, { className: 'form-wrapper', sx: { mt: 1 }, children: _jsx(FormProvider, { ...methods, children: _jsx(EditSystemForm, {}) }) }) }), _jsx(Divider, {}), _jsxs(DialogActions, { sx: { p: 3 }, children: [_jsx(Button, { onClick: methods.handleSubmit(handleFormSubmit), variant: 'contained', color: 'inherit', disabled: loading, children: loading ? 'Updating...' : t('Confirm') }), _jsx(Button, { onClick: handleClose, color: 'inherit', variant: 'outlined', children: t('Cancel') })] })] }));
};
export default EditSystemDialog;
